import React from "react"
import Modal from "react-modal"
import styled, { createGlobalStyle } from "styled-components"
import colors from "../utils/colors"
import { padding, breakpoints as bp } from "./layout"
import Icon from "./Icon"
import { ParagraphWithRemarkFragment } from "../../types/graphql-types"
import Heading from "./heading"
import clsx from "clsx"

Modal.setAppElement("#___gatsby")

export enum ModalStatus {
  SUCCESS = "success",
  ERROR = "error",
  DOWNLOAD = "download",
  CLOSE = "close",
}
interface Props {
  children: React.ReactNode
  header?: string
  headerWithRemark?: ParagraphWithRemarkFragment
  modalStatus?: ModalStatus
  onClose(): void
  full?: boolean
  showCloseCta?: boolean
  showToggleIcon?: boolean
}

const StyledIcon = styled.div`
  display: flex;
  padding: 24px;
  border-radius: 100%;
  color: ${colors.teal};
  background-color: rgba(0, 147, 154, 0.1);
  svg {
    width: 40px;
    height: 40px;
  }
  &.error {
    color: ${colors.red};
    background-color: rgba(239, 78, 82, 0.1);
  }
`

const CloseButton = styled.button`
  position: absolute;
  right: 20px;
  top: 20px;
  padding: 9px;
  color: ${colors.gray60};
  background-color: transparent;
  border: none;
  outline: none;

  &:hover {
    color: ${colors.dark};
  }

  svg {
    width: 24px;
    height: 24px;
  }
`

const StyledModal = createGlobalStyle`
  .base-modal {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    position: absolute;
    width: 640px;
    min-height: 240px;
    top: 50%;
    left: 50%;
    right: auto;
    bottom: auto;
    margin-right: -50%;
    transform: translate(-50%, -50%);
    padding: 56px;
    box-sizing: border-box;
    background-color: ${colors.white};
    border: none;
    outline: none;
    border-radius: 1rem;

    @media (max-width: ${bp.tabletMax}) {
      width: calc(100% - 2 * ${padding.desktop});
    }
    @media (max-width: ${bp.mobileMax}) {
      width: calc(100% - 2 * ${padding.mobile});
    }
    &--full{
      height: 100%;
      width: 100%;
      border-radius: unset;
      padding: ${padding.mobile} 0 0 0;
      margin: auto;
      justify-content: flex-start;
      .h4{
        font-size: 28px;
        margin-top: 24px;
        @media (min-width: ${bp.tabletMin}){
          font-size: 32px;
          margin-top: 96px;
        }
        @media (min-width: ${bp.desktopMin}){
          font-size: 48px;
          margin-top: 153px;
        }
      }
    }
    &--toggleIcon{
      ${CloseButton as any} {
        display: none;
      }
      ${StyledIcon as any} {
        padding:12px;
        svg{
          width:24px;
          height:24px;
        }
      }

      @media (min-width: ${bp.tabletMin}){
       ${CloseButton as any} {
          display: block;
        }
        ${StyledIcon as any} {
          display: none;
        }   
      }
    }
  }

  .base-modal-overlay {
    position: fixed;
    top: 0px;
    left: 0px;
    right: 0px;
    bottom: 0px;
    z-index: 20;
    background-color: ${colors.modalBackdrop};
    z-index: 20;
  }
  hr {
    border: none;
    border-top: 1px solid ${colors.gray5};
    width: 100%;
  }
  h3 {
    margin-top: 2rem;
    margin-bottom: 1rem;
    em {
      color: ${colors.teal};
      font-style: normal;
    }
  }
  div.error h3 em {
    color: ${colors.red};
  }

  .info {
    height: 36px;
    color: ${colors.gray60};
    font-size: 24px;
    font-weight: 500;
    letter-spacing: -0.5px;
    line-height: 36px;
  }
  h4 {
    font-size: 24px;
    line-height: 28px;
  }
`

const BaseModal = ({
  children,
  header,
  headerWithRemark,
  modalStatus = ModalStatus.SUCCESS,
  onClose,
  full: isFull = false,
  showToggleIcon = false,
  ...props
}: Props & ReactModal.Props) => {
  const Icons = () => {
    switch (modalStatus) {
      case (modalStatus = ModalStatus.ERROR):
        return <Icon icon="error" />
      case (modalStatus = ModalStatus.DOWNLOAD):
        return <Icon icon="download" />
      case (modalStatus = ModalStatus.CLOSE):
        return <Icon icon="close" />
      default:
        return <Icon icon="check" />
    }
  }
  const error = modalStatus === ModalStatus.ERROR

  const cNames = clsx(
    "base-modal",
    { "base-modal--full": isFull },
    { "base-modal--toggleIcon": showToggleIcon }
  )
  return (
    <Modal className={cNames} overlayClassName="base-modal-overlay" {...props}>
      <StyledModal />
      <CloseButton type="button" onClick={onClose} data-gtm-cta="modal-close">
        <Icon icon="close" />
      </CloseButton>
      <StyledIcon
        className={error ? "error" : undefined}
        onClick={showToggleIcon ? onClose : undefined}
      >
        <Icons />
      </StyledIcon>

      {header && (
        <Heading level="h1" size="h4" className={error ? "error" : undefined} content={header} />
      )}
      {headerWithRemark && (
        <div
          className={error ? "error" : undefined}
          dangerouslySetInnerHTML={{
            __html: headerWithRemark.paragraph?.childMarkdownRemark?.html!,
          }}
        />
      )}
      {children}
    </Modal>
  )
}

export default BaseModal
